import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import {
  Users as UsersIcon, FileText, Building2, Wallet, ShieldAlert, TrendingUp,
  Power, Plus, ShieldCheck, Fingerprint, CheckCircle2, XCircle, RefreshCw, Search, Boxes, Pencil, Download,
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend } from "recharts";
import { api, apiErr, money, fmtDate } from "./lib/api";
import { useAuth } from "./auth";
import { Card, Btn, Input, Label, Select, PageHeader, Badge, StatusBadge, Modal, Th, Td, Pagination } from "./ui";
import { useUrlState, useUrlNumber } from "./totals";

/* ------------------------------ Dashboard ------------------------------ */
export function Dashboard() {
  const [d, setD] = useState(null);
  const [aging, setAging] = useState(null);
  const [trend, setTrend] = useState(null);
  const nav = useNavigate();
  useEffect(() => {
    api.get("/dashboard").then((r) => setD(r.data)).catch((e) => toast.error(apiErr(e)));
    api.get("/dashboard/aging").then((r) => setAging(r.data)).catch(() => {});
    api.get("/dashboard/revenue-trend", { params: { months: 12 } }).then((r) => setTrend(r.data.series)).catch(() => {});
  }, []);
  if (!d) return <div className="text-slate-500 font-mono text-sm">Loading…</div>;
  const kpis = [
    { label: "Total Billed", value: money(d.total_billed), icon: TrendingUp, tone: "text-sky-400" },
    { label: "Collected", value: money(d.total_collected), icon: Wallet, tone: "text-emerald-400" },
    { label: "Outstanding", value: money(d.outstanding), icon: FileText, tone: "text-amber-400" },
    { label: "Pending Approvals", value: d.pending_approvals, icon: ShieldAlert, tone: "text-rose-400" },
  ];
  const counts = [
    { label: "Clients", value: d.clients, icon: UsersIcon }, { label: "Invoices", value: d.invoices, icon: FileText },
    { label: "Companies", value: d.companies, icon: Building2 }, { label: "Users", value: d.users, icon: ShieldCheck },
  ];
  return (
    <div className="animate-fade-up">
      <PageHeader title="Command Dashboard" subtitle={d.company ? `${d.company.name} · GSTIN ${d.company.gstin || "—"}` : "Financial position & security posture"} />
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-6">
        {kpis.map((k) => (
          <Card key={k.label} className="p-5" data-testid={`kpi-${k.label.toLowerCase().replace(/\s/g, "-")}`}>
            <div className="flex items-center justify-between">
              <span className="text-xs font-mono uppercase tracking-wider text-slate-400">{k.label}</span>
              <k.icon className={k.tone} size={18} />
            </div>
            <div className={`mt-3 text-3xl font-mono font-bold tracking-tight ${k.tone}`}>{k.value}</div>
          </Card>
        ))}
      </div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mt-6">
        {counts.map((c) => (
          <Card key={c.label} className="p-4 flex items-center gap-3">
            <div className="p-2 rounded-lg bg-slate-800/70"><c.icon size={18} className="text-slate-300" /></div>
            <div><div className="text-2xl font-mono font-bold text-slate-100">{c.value}</div>
              <div className="text-xs text-slate-500">{c.label}</div></div>
          </Card>
        ))}
      </div>
      {d.short_paid && (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
          <Card className="p-5 cursor-pointer hover:border-amber-500/40 transition-colors" data-testid="kpi-short-paid" onClick={() => nav("/invoices?pay=short")}>
            <div className="flex items-center justify-between"><span className="text-xs font-mono uppercase tracking-wider text-slate-400">Short-Paid Invoices</span><Wallet className="text-amber-400" size={18} /></div>
            <div className="mt-3 flex items-end gap-4"><div className="text-3xl font-mono font-bold text-amber-400">{d.short_paid.count}</div><div className="text-sm font-mono text-slate-300 pb-1">{money(d.short_paid.amount)} pending</div></div>
            <div className="mt-2 text-xs text-slate-500">{d.short_paid.tds_settled_count} shortfall = TDS (10%) · <span className="text-amber-300">{d.short_paid.genuine_short_count} genuinely short</span> · click to view</div>
          </Card>
          <Card className="p-5 cursor-pointer hover:border-rose-500/40 transition-colors" data-testid="kpi-unpaid" onClick={() => nav("/invoices?pay=unpaid")}>
            <div className="flex items-center justify-between"><span className="text-xs font-mono uppercase tracking-wider text-slate-400">Unpaid Invoices (finalized)</span><FileText className="text-rose-400" size={18} /></div>
            <div className="mt-3 flex items-end gap-4"><div className="text-3xl font-mono font-bold text-rose-400">{d.unpaid.count}</div><div className="text-sm font-mono text-slate-300 pb-1">{money(d.unpaid.amount)}</div></div>
            <div className="mt-2 text-xs text-slate-500">no payment received yet · click to view</div>
          </Card>
        </div>
      )}
      {aging && (
        <Card className="mt-6 p-5" data-testid="aging-card">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading font-semibold text-slate-200 flex items-center gap-2"><Wallet size={16} className="text-amber-400" /> Receivables Aging</h3>
            <span className="font-mono text-sm text-amber-400" data-testid="aging-total">{money(aging.total_outstanding)} outstanding</span>
          </div>
          <div className="w-full h-2.5 rounded-full overflow-hidden flex bg-slate-800 mb-4">
            {aging.buckets.map((b, i) => {
              const pct = aging.total_outstanding > 0 ? (b.amount / aging.total_outstanding) * 100 : 0;
              const col = ["bg-sky-500", "bg-amber-500", "bg-orange-500", "bg-rose-500"][i];
              return <div key={b.label} className={col} style={{ width: `${pct}%` }} title={`${b.label}: ${money(b.amount)}`} />;
            })}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {aging.buckets.map((b, i) => {
              const tone = ["text-sky-400", "text-amber-400", "text-orange-400", "text-rose-400"][i];
              return (
                <button key={b.label} onClick={() => nav(`/invoices?aging=${encodeURIComponent(b.label)}`)}
                  className="text-left rounded-lg bg-slate-900/60 border border-slate-800 hover:border-sky-600/50 hover:bg-slate-900 p-4 [transition:border-color_.2s,background-color_.2s]" data-testid={`aging-bucket-${b.label}`}>
                  <div className="text-xs font-mono uppercase tracking-wider text-slate-500">{b.label} days</div>
                  <div className={`mt-2 text-xl font-mono font-bold ${tone}`}>{money(b.amount)}</div>
                  <div className="text-xs text-slate-500 mt-1">{b.count} invoice{b.count !== 1 ? "s" : ""} →</div>
                </button>
              );
            })}
          </div>
        </Card>
      )}

      {trend && trend.length > 0 && (
        <Card className="mt-6 p-5" data-testid="revenue-trend-card">
          <h3 className="font-heading font-semibold text-slate-200 flex items-center gap-2 mb-4"><TrendingUp size={16} className="text-sky-400" /> Revenue Trend — Billed vs Collected (12 months)</h3>
          <div style={{ width: "100%", height: 280 }}>
            <ResponsiveContainer>
              <BarChart data={trend} margin={{ top: 4, right: 8, left: 8, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#1e293b" vertical={false} />
                <XAxis dataKey="month" tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(m) => m.slice(2)} />
                <YAxis tick={{ fill: "#64748b", fontSize: 11 }} tickFormatter={(v) => v >= 100000 ? `${(v / 100000).toFixed(0)}L` : v >= 1000 ? `${(v / 1000).toFixed(0)}k` : v} width={40} />
                <Tooltip formatter={(v) => money(v)} contentStyle={{ background: "#0D1220", border: "1px solid #1e293b", borderRadius: 8, fontSize: 12 }} labelStyle={{ color: "#e2e8f0" }} cursor={{ fill: "#1e293b55" }} />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                <Bar dataKey="billed" name="Billed" fill="#0EA5E9" radius={[3, 3, 0, 0]} />
                <Bar dataKey="collected" name="Collected" fill="#10b981" radius={[3, 3, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      )}

      <Card className="mt-6 overflow-hidden md:overflow-visible">
        <div className="px-5 py-3 border-b border-slate-800 font-heading font-semibold text-slate-200">Recent Invoices</div>
        <table className="w-full">
          <thead className="bg-slate-900/40"><tr><Th>Invoice #</Th><Th>Total</Th><Th>Balance</Th><Th>Status</Th></tr></thead>
          <tbody>
            {d.recent.length === 0 && <tr><Td className="text-slate-500" >No invoices yet.</Td></tr>}
            {d.recent.map((i) => (
              <tr key={i.id} className="border-t border-slate-800/60">
                <Td className="font-mono">{i.invoice_number}</Td>
                <Td className="font-mono">{money(i.total, i.currency)}</Td>
                <Td className="font-mono">{money(i.balance, i.currency)}</Td>
                <Td><StatusBadge status={i.status} /></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ------------------------------ Clients ------------------------------ */
const emptyClient = { name: "", company_name: "", gstin: "", address: "", city: "", pincode: "", state: "", state_code: "", country: "India", email: "", phone: "", currency: "INR" };
export function Clients() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [skip, setSkip] = useUrlNumber("skip", 0);
  const [q, setQ] = useUrlState("q");
  const [open, setOpen] = useState(false);
  const [editId, setEditId] = useState(null);
  const [missing, setMissing] = useUrlState("missing");
  const [states, setStates] = useState([]);
  const [f, setF] = useState(emptyClient);
  const load = () => api.get("/clients", { params: { q, skip, limit: 50, ...(missing ? { missing } : {}) } }).then((r) => { setRows(r.data.items); setTotal(r.data.total); });
  useEffect(() => { api.get("/states").then((r) => setStates(r.data)).catch(() => {}); }, []);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); /* eslint-disable-next-line */ }, [q, skip, missing]);
  const openEdit = (c) => { setEditId(c.id); setF({ ...emptyClient, ...Object.fromEntries(Object.keys(emptyClient).map((k) => [k, c[k] ?? emptyClient[k]])) }); setOpen(true); };
  const onState = (name) => { const st = states.find((x) => x.name === name); setF({ ...f, state: name, state_code: st ? st.code : f.state_code }); };
  const save = async () => {
    if (!f.name) return toast.error("Name required");
    try {
      if (editId) { await api.patch(`/clients/${editId}`, f); toast.success("Client updated"); }
      else { await api.post("/clients", f); toast.success("Client added"); setSkip(0); }
      setOpen(false); setEditId(null); setF(emptyClient); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="animate-fade-up">
      <PageHeader title="Clients" subtitle={`${total} client${total !== 1 ? "s" : ""} · Infomedia Web Solutions`}
        actions={<Btn data-testid="add-client-btn" onClick={() => { setEditId(null); setF(emptyClient); setOpen(true); }}><Plus size={16} /> New Client</Btn>} />
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <div className="relative max-w-md flex-1 min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-500" />
          <Input data-testid="client-search" className="pl-9" placeholder="Search by name / GSTIN / email…"
            value={q} onChange={(e) => { setQ(e.target.value); setSkip(0); }} />
        </div>
        <button data-testid="client-filter-missing" onClick={() => { setMissing(missing ? "" : "address"); setSkip(0); }}
          className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${missing ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-slate-800/40 text-slate-400 border-slate-700 hover:text-slate-200"}`}>Missing city / state</button>
      </div>
      <Card className="overflow-x-auto md:overflow-visible">
        <table className="w-full min-w-[640px]">
          <thead className="bg-slate-900/40"><tr><Th>Name</Th><Th>Company</Th><Th>GSTIN</Th><Th>City</Th><Th>State</Th><Th>Country</Th><Th>Statement</Th><Th></Th></tr></thead>
          <tbody>
            {rows.map((c) => (
              <tr key={c.id} className="border-t border-slate-800/60 hover:bg-slate-800/30" data-testid={`client-row-${c.id}`}>
                <Td className="font-medium">{c.name}</Td><Td className="text-slate-400">{c.company_name || "—"}</Td>
                <Td className="font-mono text-xs">{c.gstin || "—"}</Td>
                <Td className={c.city ? "text-slate-400" : "text-amber-400/70 text-xs"}>{c.city || "missing"}</Td>
                <Td className={c.state ? "text-slate-400" : "text-amber-400/70 text-xs"}>{c.state || "missing"}</Td><Td>{c.country}</Td>
                <Td><a data-testid={`client-statement-${c.id}`} href={`${process.env.REACT_APP_BACKEND_URL}/api/clients/${c.id}/statement`} target="_blank" rel="noreferrer"
                  className="inline-flex items-center gap-1.5 text-sky-400 hover:text-sky-300 text-xs font-medium"><FileText size={14} /> PDF</a></Td>
                <Td><button data-testid={`edit-client-${c.id}`} title="Edit client" onClick={() => openEdit(c)} className="text-slate-400 hover:text-sky-300"><Pencil size={14} /></button></Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><Td className="text-slate-500">No clients found.</Td></tr>}
          </tbody>
        </table>
        <Pagination skip={skip} limit={50} total={total} onPage={setSkip} />
      </Card>
      <Modal open={open} onClose={() => { setOpen(false); setEditId(null); }} title={editId ? `Edit Client — ${f.name}` : "New Client"}>
        <div className="grid grid-cols-2 gap-3">
          {[["name", "Name *"], ["company_name", "Company"], ["gstin", "GSTIN"], ["email", "Email"], ["phone", "Phone"]].map(([k, l]) => (
            <div key={k} className={k === "name" ? "col-span-2" : ""}>
              <Label>{l}</Label>
              <Input data-testid={`client-${k}`} value={f[k]} onChange={(e) => setF({ ...f, [k]: e.target.value })} />
            </div>
          ))}
          <div className="col-span-2"><Label>Address</Label>
            <textarea data-testid="client-address" rows={2} value={f.address} onChange={(e) => setF({ ...f, address: e.target.value })}
              className="w-full bg-slate-950/60 border border-slate-700 focus:border-sky-500 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none" /></div>
          <div><Label>City</Label><Input data-testid="client-city" value={f.city} onChange={(e) => setF({ ...f, city: e.target.value })} /></div>
          <div><Label>PIN Code</Label><Input data-testid="client-pincode" value={f.pincode} onChange={(e) => setF({ ...f, pincode: e.target.value })} /></div>
          <div><Label>State</Label>
            <Input data-testid="client-state" list="client-state-list" value={f.state} onChange={(e) => onState(e.target.value)} />
            <datalist id="client-state-list">{states.map((st) => <option key={st.code} value={st.name} />)}</datalist></div>
          <div><Label>State Code</Label><Input data-testid="client-state_code" value={f.state_code} onChange={(e) => setF({ ...f, state_code: e.target.value })} /></div>
          <div><Label>Country</Label><Input data-testid="client-country" value={f.country} onChange={(e) => setF({ ...f, country: e.target.value })} /></div>
          <div><Label>Currency</Label>
            <Select data-testid="client-currency" value={f.currency} onChange={(e) => setF({ ...f, currency: e.target.value })}>
              {["INR", "USD", "EUR", "GBP", "AUD", "AED"].map((c) => <option key={c}>{c}</option>)}
            </Select>
          </div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="save-client-btn" onClick={save}>{editId ? "Update Client" : "Save Client"}</Btn></div>
      </Modal>
    </div>
  );
}

/* ------------------------------ Users + Offboarding ------------------------------ */
export function UsersPage() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);
  const [kill, setKill] = useState(null);
  const [f, setF] = useState({ email: "", name: "", password: "", role: "staff" });
  const load = () => api.get("/users").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const create = async () => {
    try { await api.post("/users", f); toast.success("User created"); setOpen(false); setF({ email: "", name: "", password: "", role: "staff" }); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const doKill = async () => {
    try { await api.post(`/users/${kill.id}/offboard`); toast.success(`${kill.email} offboarded — all sessions revoked`); setKill(null); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const toggleRole = async (u, role) => { try { await api.patch(`/users/${u.id}`, { role }); load(); } catch (e) { toast.error(apiErr(e)); } };
  const isSuper = user.role === "super_admin";
  return (
    <div className="animate-fade-up">
      <PageHeader title="Users & Access" subtitle="Named accounts, roles and instant offboarding"
        actions={isSuper && <Btn data-testid="add-user-btn" onClick={() => setOpen(true)}><Plus size={16} /> New User</Btn>} />
      <Card className="overflow-x-auto md:overflow-visible">
        <table className="w-full min-w-[720px]">
          <thead className="bg-slate-900/40"><tr><Th>Name</Th><Th>Email</Th><Th>Role</Th><Th>2FA</Th><Th>Status</Th><Th>Action</Th></tr></thead>
          <tbody>
            {rows.map((u) => (
              <tr key={u.id} className="border-t border-slate-800/60" data-testid={`user-row-${u.id}`}>
                <Td className="font-medium">{u.name}</Td><Td className="font-mono text-xs">{u.email}</Td>
                <Td>{isSuper ? (
                  <Select data-testid={`role-${u.id}`} className="w-36" value={u.role} onChange={(e) => toggleRole(u, e.target.value)}>
                    <option value="super_admin">super_admin</option><option value="admin">admin</option><option value="staff">staff</option>
                  </Select>) : <Badge tone="purple">{u.role}</Badge>}</Td>
                <Td>{u.totp_enabled ? <Badge tone="emerald">ON</Badge> : <Badge>OFF</Badge>}</Td>
                <Td>{u.is_active ? <Badge tone="emerald">active</Badge> : <Badge tone="rose">disabled</Badge>}</Td>
                <Td>{isSuper && u.email !== user.email && u.is_active &&
                  <Btn data-testid={`offboard-${u.id}`} variant="danger" className="px-3 py-1.5 text-xs" onClick={() => setKill(u)}><Power size={14} /> Offboard</Btn>}</Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Modal open={open} onClose={() => setOpen(false)} title="New User">
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="user-name" value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} /></div>
          <div><Label>Email</Label><Input data-testid="user-email" value={f.email} onChange={(e) => setF({ ...f, email: e.target.value })} /></div>
          <div><Label>Temp Password</Label><Input data-testid="user-password" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>
          <div><Label>Role</Label><Select data-testid="user-role" value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
            <option value="staff">staff</option><option value="admin">admin</option><option value="super_admin">super_admin</option></Select></div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setOpen(false)}>Cancel</Btn>
          <Btn data-testid="save-user-btn" onClick={create}>Create</Btn></div>
      </Modal>

      <Modal open={!!kill} onClose={() => setKill(null)} title="⚠ Offboard User" danger>
        <p className="text-slate-300 text-sm">This immediately <b>disables the account and revokes every active session and token</b> for
          <span className="font-mono text-rose-300"> {kill?.email}</span>. They are locked out instantly. This is logged in the audit trail.</p>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setKill(null)}>Cancel</Btn>
          <Btn data-testid="confirm-offboard-btn" variant="danger" onClick={doKill}><Power size={16} /> Revoke Access Now</Btn></div>
      </Modal>
    </div>
  );
}

/* ------------------------------ Approvals ------------------------------ */
export function Approvals() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const load = () => api.get("/approvals").then((r) => setRows(r.data)).catch((e) => toast.error(apiErr(e)));
  useEffect(() => { load(); }, []);
  const decide = async (id, decision) => {
    try { await api.post(`/approvals/${id}/decide`, { decision }); toast.success(`Request ${decision}d`); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="animate-fade-up">
      <PageHeader title="Approval Queue" subtitle="Sensitive actions require a second sign-off" />
      <Card className="overflow-x-auto md:overflow-visible">
        <table className="w-full min-w-[720px]">
          <thead className="bg-slate-900/40"><tr><Th>Action</Th><Th>Target</Th><Th>Requested by</Th><Th>Reason</Th><Th>Status</Th><Th>Decision</Th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.id} className="border-t border-slate-800/60">
                <Td><Badge tone="amber">{a.action}</Badge></Td><Td className="font-mono text-xs">{a.entity_id}</Td>
                <Td className="text-slate-400">{a.requested_by}</Td><Td className="text-slate-400">{a.reason || "—"}</Td>
                <Td><Badge tone={a.status === "approved" ? "emerald" : a.status === "rejected" ? "rose" : "amber"}>{a.status}</Badge></Td>
                <Td>{a.status === "pending" && user.role === "super_admin" ? (
                  <div className="flex gap-2">
                    <Btn data-testid={`approve-${a.id}`} className="px-3 py-1.5 text-xs" onClick={() => decide(a.id, "approve")}><CheckCircle2 size={14} /> Approve</Btn>
                    <Btn data-testid={`reject-${a.id}`} variant="danger" className="px-3 py-1.5 text-xs" onClick={() => decide(a.id, "reject")}><XCircle size={14} /> Reject</Btn>
                  </div>) : <span className="text-slate-500 text-xs">{a.decided_by || "—"}</span>}</Td>
              </tr>
            ))}
            {rows.length === 0 && <tr><Td className="text-slate-500">No requests.</Td></tr>}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ------------------------------ Audit ------------------------------ */
export function Audit() {
  const [rows, setRows] = useState([]);
  const [chk, setChk] = useState(null);
  const load = () => api.get("/audit").then((r) => setRows(r.data));
  useEffect(() => { load(); }, []);
  const verify = async () => { try { const { data } = await api.get("/audit/verify"); setChk(data); toast[data.intact ? "success" : "error"](data.intact ? `Chain intact (${data.entries} entries)` : `TAMPER at seq ${data.broken_at_seq}`); } catch (e) { toast.error(apiErr(e)); } };
  return (
    <div className="animate-fade-up">
      <PageHeader title="Audit Trail" subtitle="Tamper-evident, hash-chained log of every action"
        actions={<Btn data-testid="verify-audit-btn" variant="ghost" onClick={verify}><ShieldCheck size={16} /> Verify Integrity</Btn>} />
      {chk && <Card className={`p-3 mb-4 ${chk.intact ? "border-emerald-700/50" : "border-rose-700/50"}`}>
        <span className={`font-mono text-sm ${chk.intact ? "text-emerald-400" : "text-rose-400"}`}>
          {chk.intact ? `✓ Verified — ${chk.entries} entries, unbroken hash chain` : `✗ Chain broken at seq ${chk.broken_at_seq}`}</span></Card>}
      <Card className="overflow-x-auto md:overflow-visible">
        <table className="w-full min-w-[880px]">
          <thead className="bg-slate-900/40"><tr><Th>#</Th><Th>Time (UTC)</Th><Th>Actor</Th><Th>Action</Th><Th>Summary</Th><Th>IP</Th><Th>Hash</Th></tr></thead>
          <tbody>
            {rows.map((a) => (
              <tr key={a.seq} className="border-t border-slate-800/60">
                <Td className="font-mono text-slate-500">{a.seq}</Td>
                <Td className="font-mono text-xs text-slate-400">{a.at ? `${fmtDate(a.at)} ${String(a.at).slice(11, 19)}` : "—"}</Td>
                <Td className="text-xs">{a.actor_email || "—"}</Td>
                <Td><Badge tone={a.action.includes("offboard") || a.action.includes("failed") ? "rose" : a.action.includes("login") ? "sky" : "slate"}>{a.action}</Badge></Td>
                <Td className="text-slate-400 text-xs max-w-[280px] truncate">{a.summary}</Td>
                <Td><span className="font-mono text-xs bg-indigo-950/50 text-indigo-300 border border-indigo-800/40 px-2 py-0.5 rounded">{a.ip}</span></Td>
                <Td><span className="font-mono text-[11px] text-slate-500 select-all" title={a.hash}>{(a.hash || "").slice(0, 10)}…</span></Td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

/* ------------------------------ Masters ------------------------------ */
export function Masters() {
  const { user } = useAuth();
  const isAdmin = ["super_admin", "admin"].includes(user.role);
  const [services, setServices] = useState([]);
  const [taxes, setTaxes] = useState([]);
  const [states, setStates] = useState([]);
  const [svcOpen, setSvcOpen] = useState(false);
  const [taxOpen, setTaxOpen] = useState(false);
  const [svc, setSvc] = useState({ name: "", sac_code: "", description: "" });
  const [tax, setTax] = useState({ name: "", rate: 18, cgst: 9, sgst: 9, igst: 18 });
  const [editSvcId, setEditSvcId] = useState(null);
  const [editTaxId, setEditTaxId] = useState(null);
  const [companies, setCompanies] = useState([]);
  const [ftOpen, setFtOpen] = useState(false);
  const [ftComp, setFtComp] = useState(null);
  const [ft, setFt] = useState({ invoice_footer: "", export_footer: "" });
  const load = () => {
    api.get("/services").then((r) => setServices(r.data));
    api.get("/tax-rates").then((r) => setTaxes(r.data));
    api.get("/states").then((r) => setStates(r.data));
    api.get("/companies").then((r) => setCompanies(r.data));
  };
  useEffect(() => { load(); }, []);
  const openFooters = (c) => { setFtComp(c); setFt({ invoice_footer: c.invoice_footer || "", export_footer: c.export_footer || "" }); setFtOpen(true); };
  const saveFooters = async () => {
    try { await api.patch(`/companies/${ftComp.id}/footers`, ft); toast.success("Bank / payment details saved"); setFtOpen(false); load(); }
    catch (e) { toast.error(apiErr(e)); }
  };
  const openAddSvc = () => { setEditSvcId(null); setSvc({ name: "", sac_code: "", description: "" }); setSvcOpen(true); };
  const openEditSvc = (s) => { setEditSvcId(s.id); setSvc({ name: s.name || "", sac_code: s.sac_code || "", description: s.description || "" }); setSvcOpen(true); };
  const saveSvc = async () => {
    if (!svc.name) return toast.error("Name required");
    try {
      if (editSvcId) { await api.patch(`/services/${editSvcId}`, svc); toast.success("Service updated"); }
      else { await api.post("/services", svc); toast.success("Service added"); }
      setSvcOpen(false); setEditSvcId(null); setSvc({ name: "", sac_code: "", description: "" }); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  const openAddTax = () => { setEditTaxId(null); setTax({ name: "", rate: 18, cgst: 9, sgst: 9, igst: 18 }); setTaxOpen(true); };
  const openEditTax = (t) => { setEditTaxId(t.id); setTax({ name: t.name || "", rate: t.rate, cgst: t.cgst, sgst: t.sgst, igst: t.igst }); setTaxOpen(true); };
  const saveTax = async () => {
    if (!tax.name) return toast.error("Name required");
    const payload = { ...tax, rate: Number(tax.rate), cgst: Number(tax.cgst), sgst: Number(tax.sgst), igst: Number(tax.igst) };
    try {
      if (editTaxId) { await api.patch(`/tax-rates/${editTaxId}`, payload); toast.success("Tax rate updated"); }
      else { await api.post("/tax-rates", payload); toast.success("Tax rate added"); }
      setTaxOpen(false); setEditTaxId(null); load();
    } catch (e) { toast.error(apiErr(e)); }
  };
  return (
    <div className="animate-fade-up">
      <PageHeader title="Masters" subtitle="Service catalog, GST rates, state codes & company bank details — used when creating invoices" />
      <Card className="overflow-hidden md:overflow-visible mb-6" data-testid="company-footers-card">
        <div className="px-5 py-3 border-b border-slate-800 font-heading font-semibold text-slate-200 flex items-center gap-2"><Building2 size={16} className="text-sky-400" /> Company Bank / Payment Details (invoice footers)</div>
        <table className="w-full"><thead className="bg-slate-900/40"><tr><Th>Company</Th><Th>Domestic (India) footer</Th><Th>Exports footer</Th>{isAdmin && <Th className="text-right">Edit</Th>}</tr></thead>
          <tbody>{companies.map((c) => (
            <tr key={c.id} className="border-t border-slate-800/60" data-testid={`company-footer-row-${c.id}`}>
              <Td className="font-medium">{c.name}</Td>
              <Td className="text-xs text-slate-400 max-w-[320px] truncate">{c.invoice_footer ? c.invoice_footer.split("\n")[1] || c.invoice_footer.split("\n")[0] : <span className="text-slate-600">— not set —</span>}</Td>
              <Td className="text-xs text-slate-400 max-w-[320px] truncate">{c.export_footer ? (c.export_footer.match(/A\/c no\.?\s*-?\s*[\d]+/i) || [c.export_footer.split("\n")[0]])[0] : <span className="text-slate-600">— not set —</span>}</Td>
              {isAdmin && <Td className="text-right"><button data-testid={`edit-footers-${c.id}`} onClick={() => openFooters(c)} className="text-slate-400 hover:text-sky-300"><Pencil size={14} /></button></Td>}</tr>))}</tbody>
        </table>
      </Card>
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <Card className="overflow-hidden md:overflow-visible">
          <div className="px-5 py-3 border-b border-slate-800 font-heading font-semibold text-slate-200 flex items-center justify-between">
            <span className="flex items-center gap-2"><Boxes size={16} className="text-sky-400" /> Service Catalog ({services.length})</span>
            {isAdmin && <Btn data-testid="add-service-btn" variant="ghost" className="px-3 py-1.5 text-xs" onClick={openAddSvc}><Plus size={14} /> Add</Btn>}
          </div>
          <div className="max-h-[420px] overflow-auto">
            <table className="w-full"><thead className="bg-slate-900/40 sticky top-0"><tr><Th>Service</Th><Th>SAC</Th>{isAdmin && <Th className="text-right">Edit</Th>}</tr></thead>
              <tbody>{services.map((s) => (
                <tr key={s.id} className="border-t border-slate-800/60" data-testid={`service-row-${s.id}`}>
                  <Td>{s.name}</Td><Td className="font-mono text-xs">{s.sac_code || "—"}</Td>
                  {isAdmin && <Td className="text-right"><button data-testid={`edit-service-${s.id}`} onClick={() => openEditSvc(s)} className="text-slate-400 hover:text-sky-300"><Pencil size={14} /></button></Td>}</tr>))}</tbody>
            </table>
          </div>
        </Card>
        <div className="space-y-6">
          <Card className="overflow-hidden md:overflow-visible">
            <div className="px-5 py-3 border-b border-slate-800 font-heading font-semibold text-slate-200 flex items-center justify-between">
              <span>GST Rates ({taxes.length})</span>
              {isAdmin && <Btn data-testid="add-tax-btn" variant="ghost" className="px-3 py-1.5 text-xs" onClick={openAddTax}><Plus size={14} /> Add</Btn>}
            </div>
            <table className="w-full"><thead className="bg-slate-900/40"><tr><Th>Name</Th><Th>Rate</Th><Th>CGST</Th><Th>SGST</Th><Th>IGST</Th>{isAdmin && <Th className="text-right">Edit</Th>}</tr></thead>
              <tbody>{taxes.map((t) => (
                <tr key={t.id} className="border-t border-slate-800/60" data-testid={`tax-row-${t.id}`}>
                  <Td>{t.name}</Td><Td className="font-mono">{t.rate}%</Td><Td className="font-mono">{t.cgst}%</Td><Td className="font-mono">{t.sgst}%</Td><Td className="font-mono">{t.igst}%</Td>
                  {isAdmin && <Td className="text-right"><button data-testid={`edit-tax-${t.id}`} onClick={() => openEditTax(t)} className="text-slate-400 hover:text-sky-300"><Pencil size={14} /></button></Td>}</tr>))}</tbody>
            </table>
          </Card>
          <Card className="overflow-hidden md:overflow-visible">
            <div className="px-5 py-3 border-b border-slate-800 font-heading font-semibold text-slate-200">State Codes ({states.length})</div>
            <div className="max-h-[220px] overflow-auto p-3 flex flex-wrap gap-2">
              {states.map((s) => <Badge key={s.id} tone="indigo">{s.code} · {s.name}</Badge>)}
            </div>
          </Card>
        </div>
      </div>

      <Modal open={svcOpen} onClose={() => { setSvcOpen(false); setEditSvcId(null); }} title={editSvcId ? "Edit Service" : "New Service"}>
        <div className="space-y-3">
          <div><Label>Name</Label><Input data-testid="svc-name" value={svc.name} onChange={(e) => setSvc({ ...svc, name: e.target.value })} /></div>
          <div><Label>SAC / HSN Code</Label><Input data-testid="svc-sac" value={svc.sac_code} onChange={(e) => setSvc({ ...svc, sac_code: e.target.value })} /></div>
          <div><Label>Description</Label><Input data-testid="svc-desc" value={svc.description} onChange={(e) => setSvc({ ...svc, description: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setSvcOpen(false)}>Cancel</Btn><Btn data-testid="save-service-btn" onClick={saveSvc}>Save</Btn></div>
      </Modal>
      <Modal open={taxOpen} onClose={() => { setTaxOpen(false); setEditTaxId(null); }} title={editTaxId ? "Edit GST Rate" : "New GST Rate"}>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Name</Label><Input data-testid="tax-name" value={tax.name} onChange={(e) => setTax({ ...tax, name: e.target.value })} placeholder="GST 18%" /></div>
          <div><Label>Total Rate %</Label><Input data-testid="tax-rate" type="number" value={tax.rate} onChange={(e) => setTax({ ...tax, rate: e.target.value, igst: e.target.value, cgst: e.target.value / 2, sgst: e.target.value / 2 })} /></div>
          <div><Label>IGST %</Label><Input type="number" value={tax.igst} onChange={(e) => setTax({ ...tax, igst: e.target.value })} /></div>
          <div><Label>CGST %</Label><Input type="number" value={tax.cgst} onChange={(e) => setTax({ ...tax, cgst: e.target.value })} /></div>
          <div><Label>SGST %</Label><Input type="number" value={tax.sgst} onChange={(e) => setTax({ ...tax, sgst: e.target.value })} /></div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setTaxOpen(false)}>Cancel</Btn><Btn data-testid="save-tax-btn" onClick={saveTax}>Save</Btn></div>
      </Modal>
      <Modal open={ftOpen} onClose={() => setFtOpen(false)} title={`Bank / Payment Details — ${ftComp?.name || ""}`}>
        <div className="space-y-4">
          <div><Label>Domestic invoice footer (India) — printed on regular Tax Invoices</Label>
            <textarea data-testid="ft-domestic" rows={6} value={ft.invoice_footer} onChange={(e) => setFt({ ...ft, invoice_footer: e.target.value })}
              placeholder={"How to make payments:\nTransfer funds to our HDFC Bank account - No. ... IFSC - ..."}
              className="w-full bg-slate-950/60 border border-slate-700 focus:border-sky-500 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none font-mono" /></div>
          <div><Label>Export invoice footer — printed on Export Invoices</Label>
            <textarea data-testid="ft-export" rows={7} value={ft.export_footer} onChange={(e) => setFt({ ...ft, export_footer: e.target.value })}
              placeholder={"Please arrange to remit to the following details:\nBeneficiary Bank Name\nHDFC Bank Ltd, ...\nBeneficiary Bank SWIFT Code - ...\nBeneficiary Name - ...\nBeneficiary A/c no. - ..."}
              className="w-full bg-slate-950/60 border border-slate-700 focus:border-sky-500 rounded-lg px-3 py-2 text-sm text-slate-100 outline-none font-mono" /></div>
          <div className="text-xs text-slate-500">First line prints in bold. One line per row; leave a box empty to print no footer on that invoice type.</div>
        </div>
        <div className="flex justify-end gap-2 mt-5"><Btn variant="ghost" onClick={() => setFtOpen(false)}>Cancel</Btn><Btn data-testid="save-footers-btn" onClick={saveFooters}>Save</Btn></div>
      </Modal>
    </div>
  );
}

/* ------------------------------ TDS Receivable ------------------------------ */
export function TdsReport() {
  const [data, setData] = useState(null);
  const [period, setPeriod] = useState("fy");
  const [fy, setFy] = useState("");
  const [month, setMonth] = useState(new Date().toISOString().slice(0, 7));
  const [from, setFrom] = useState("");
  const [to, setTo] = useState("");
  useEffect(() => {
    const params = { period, ...(period === "fy" && fy ? { fy } : {}), ...(period === "month" ? { month } : {}), ...(period === "custom" ? { date_from: from, date_to: to } : {}) };
    api.get("/reports/tds", { params }).then((r) => { setData(r.data); if (!fy && r.data.current_fy) setFy(r.data.period?.fy || r.data.current_fy); }).catch((e) => toast.error(apiErr(e)));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, fy, month, from, to]);
  const rows = data?.rows || [];
  const fyOptions = data?.fy_options || [];
  const periodLabel = period === "fy" ? `FY ${data?.period?.fy || ""}` : period === "month" ? `Month ${month}` : period === "custom" ? `${from || "…"} → ${to || "…"}` : "All time";
  const exportCsv = () => {
    const head = ["Date", "Invoice #", "Client", "Received", "TDS Recoverable", "Method", "Reference"];
    const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
    const lines = [head.join(",")].concat(rows.map((r) => [r.date, r.invoice_number, esc(r.client), r.received, r.tds, r.method, esc(r.reference)].join(",")));
    lines.push(["", "", esc("TOTAL"), rows.reduce((s, r) => s + (Number(r.received) || 0), 0).toFixed(2), rows.reduce((s, r) => s + (Number(r.tds) || 0), 0).toFixed(2), "", ""].join(","));
    const url = URL.createObjectURL(new Blob([lines.join("\n")], { type: "text/csv" }));
    const a = document.createElement("a");
    a.href = url; a.download = `tds_receivable_${periodLabel.replace(/[^\w-]+/g, "_")}.csv`;
    document.body.appendChild(a); a.click(); a.remove(); URL.revokeObjectURL(url);
  };
  return (
    <div className="animate-fade-up" data-testid="tds-report-page">
      <PageHeader title="TDS Receivable" subtitle="Tax deducted at source by clients — recoverable against your tax liability"
        actions={<Btn data-testid="tds-export-csv" variant="ghost" onClick={exportCsv} disabled={!rows.length}><Download size={15} /> CSV</Btn>} />
      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="flex items-center gap-1.5">
          {[["fy", "Financial Year"], ["month", "Month"], ["custom", "Custom Dates"], ["all", "All Time"]].map(([v, l]) => (
            <button key={v} data-testid={`tds-period-${v}`} onClick={() => setPeriod(v)}
              className={`px-3 py-2 rounded-lg text-xs font-medium border transition-colors ${period === v ? "bg-amber-500/20 text-amber-300 border-amber-500/40" : "bg-slate-800/40 text-slate-400 border-slate-700 hover:text-slate-200"}`}>{l}</button>))}
        </div>
        {period === "fy" && <div><Label>Financial Year</Label>
          <Select data-testid="tds-fy" className="w-36" value={fy} onChange={(e) => setFy(e.target.value)}>
            {(fyOptions.length ? fyOptions : [data?.current_fy]).filter(Boolean).map((o) => <option key={o} value={o}>{o}{o === data?.current_fy ? " (current)" : ""}</option>)}
          </Select></div>}
        {period === "month" && <div><Label>Month</Label><Input data-testid="tds-month" type="month" className="w-44" value={month} onChange={(e) => setMonth(e.target.value)} /></div>}
        {period === "custom" && <><div><Label>From</Label><Input data-testid="tds-from" type="date" className="w-40" value={from} onChange={(e) => setFrom(e.target.value)} /></div>
          <div><Label>To</Label><Input data-testid="tds-to" type="date" className="w-40" value={to} onChange={(e) => setTo(e.target.value)} /></div></>}
      </div>
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
        <Card className="p-5" data-testid="tds-total-card">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-400">Total TDS Recoverable</div>
          <div className="mt-2 text-3xl font-mono font-bold text-amber-400">{money(data?.total || 0)}</div>
          <div className="text-xs text-slate-500 mt-1">{periodLabel} · {data?.count || 0} entries</div>
        </Card>
        <Card className="p-5" data-testid="tds-received-card">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-400">Cash Received (same entries)</div>
          <div className="mt-2 text-3xl font-mono font-bold text-emerald-400">{money(data?.received || 0)}</div>
          <div className="text-xs text-slate-500 mt-1">net of TDS</div>
        </Card>
        <Card className="p-5" data-testid="tds-gross-card">
          <div className="text-xs font-mono uppercase tracking-wider text-slate-400">Gross Applied</div>
          <div className="mt-2 text-3xl font-mono font-bold text-slate-100">{money((data?.received || 0) + (data?.total || 0))}</div>
          <div className="text-xs text-slate-500 mt-1">received + TDS · {data?.period?.from} → {data?.period?.to}</div>
        </Card>
      </div>
      <Card className="overflow-hidden md:overflow-visible">
        <div className="overflow-x-auto md:overflow-visible"><table className="w-full min-w-[720px]">
          <thead className="bg-slate-900/40"><tr><Th>Date</Th><Th>Invoice #</Th><Th>Client</Th><Th>Received</Th><Th>TDS Recoverable</Th><Th>Method</Th><Th>Reference</Th></tr></thead>
          <tbody>
            {rows.map((r, i) => (
              <tr key={i} className="border-t border-slate-800/60" data-testid={`tds-row-${i}`}>
                <Td className="text-slate-400">{fmtDate(r.date)}</Td><Td className="font-mono text-xs">{r.invoice_number}</Td>
                <Td>{r.client || "—"}</Td><Td className="font-mono text-emerald-400">{money(r.received)}</Td>
                <Td className="font-mono text-amber-300">{money(r.tds)}</Td><Td>{r.method}</Td><Td className="font-mono text-xs">{r.reference || "—"}</Td></tr>))}
            {!rows.length && <tr><Td className="text-slate-500" colSpan={7}>No TDS recorded yet.</Td></tr>}
          </tbody>
          {rows.length > 0 && <tfoot><tr className="border-t-2 border-slate-600 bg-slate-900/60" data-testid="tds-totals-row">
            <Td className="font-semibold text-slate-200" colSpan={3}>Total ({rows.length} entries)</Td>
            <Td className="font-mono font-bold text-emerald-400" data-testid="tds-total-received">{money(rows.reduce((s, r) => s + (Number(r.received) || 0), 0))}</Td>
            <Td className="font-mono font-bold text-amber-300" data-testid="tds-total-tds">{money(rows.reduce((s, r) => s + (Number(r.tds) || 0), 0))}</Td>
            <Td colSpan={2}></Td></tr></tfoot>}
        </table></div>
      </Card>
    </div>
  );
}


/* ------------------------------ Settings + 2FA ------------------------------ */
export function Settings() {
  const { user, refresh } = useAuth();
  const [s, setS] = useState(null);
  const [setup, setSetup] = useState(null);
  const [code, setCode] = useState("");
  const [ips, setIps] = useState([]);
  const [newIp, setNewIp] = useState("");
  const loadSettings = () => api.get("/settings").then((r) => { setS(r.data); setIps(r.data.office_ips || []); });
  useEffect(() => { loadSettings(); }, []);
  const begin = async () => { try { const { data } = await api.post("/auth/2fa/setup"); setSetup(data); } catch (e) { toast.error(apiErr(e)); } };
  const enable = async () => { try { await api.post("/auth/2fa/enable", { totp: code }); toast.success("2FA enabled"); setSetup(null); setCode(""); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const disable = async () => { try { await api.post("/auth/2fa/disable"); toast.success("2FA disabled"); refresh(); } catch (e) { toast.error(apiErr(e)); } };
  const addThisIp = () => { if (s?.your_ip && !ips.includes(s.your_ip)) setIps([...ips, s.your_ip]); };
  const addIp = () => { const v = newIp.trim(); if (v && !ips.includes(v)) { setIps([...ips, v]); setNewIp(""); } };
  const removeIp = (ip) => setIps(ips.filter((x) => x !== ip));
  const saveIps = async () => { try { await api.patch("/settings", { office_ips: ips }); toast.success("Office IPs saved"); loadSettings(); } catch (e) { toast.error(apiErr(e)); } };
  const isSuper = user.role === "super_admin";
  return (
    <div className="animate-fade-up max-w-3xl">
      <PageHeader title="Security Settings" subtitle="Two-factor authentication & office network" />
      <Card className="p-6 mb-6">
        <div className="flex items-center gap-3 mb-4"><Fingerprint className="text-sky-400" /><h3 className="font-heading font-semibold text-slate-100">Two-Factor Authentication (TOTP)</h3>
          {user.totp_enabled ? <Badge tone="emerald">ENABLED</Badge> : <Badge>DISABLED</Badge>}</div>
        <p className="text-sm text-slate-400 mb-4">2FA is only prompted when you sign in from <b>outside your office network</b>. From an office IP it is skipped for convenience.</p>
        {!user.totp_enabled && !setup && <Btn data-testid="setup-2fa-btn" onClick={begin}><ShieldCheck size={16} /> Set up 2FA</Btn>}
        {user.totp_enabled && <Btn data-testid="disable-2fa-btn" variant="danger" onClick={disable}>Disable 2FA</Btn>}
        {setup && (
          <div className="mt-4 p-4 rounded-lg bg-slate-950/60 border border-slate-800">
            <p className="text-sm text-slate-300 mb-2">Add this secret to Google Authenticator, then enter the 6-digit code:</p>
            <div className="font-mono text-lg text-sky-300 bg-slate-900 border border-slate-700 rounded px-3 py-2 mb-3 select-all break-all">{setup.secret}</div>
            <div className="flex gap-2 items-end">
              <div><Label>6-digit code</Label><Input data-testid="2fa-code" className="w-40 font-mono tracking-widest" value={code} onChange={(e) => setCode(e.target.value)} placeholder="000000" /></div>
              <Btn data-testid="enable-2fa-btn" onClick={enable}>Verify & Enable</Btn>
            </div>
          </div>
        )}
      </Card>
      {s && (
        <Card className="p-6">
          <div className="flex items-center gap-3 mb-3"><ShieldCheck className="text-emerald-400" /><h3 className="font-heading font-semibold text-slate-100">Office Network — 2FA Bypass</h3></div>
          <p className="text-sm text-slate-400 mb-4">Your current IP: <span className="font-mono text-slate-200">{s.your_ip}</span>
            {" "}{s.in_office ? <Badge tone="emerald">OFFICE</Badge> : <Badge tone="amber">REMOTE</Badge>}</p>
          <Label>Allow-listed office IPs (2FA skipped here)</Label>
          <div className="flex flex-wrap gap-2 mb-3">
            {ips.length === 0 && <span className="text-slate-500 text-sm">None — 2FA required everywhere (when enabled).</span>}
            {ips.map((ip) => (
              <span key={ip} className="inline-flex items-center gap-2 font-mono text-xs bg-indigo-950/50 text-indigo-300 border border-indigo-800/40 px-2 py-1 rounded" data-testid={`ip-${ip}`}>
                {ip}{isSuper && <button onClick={() => removeIp(ip)} className="text-rose-400 hover:text-rose-300">×</button>}
              </span>
            ))}
          </div>
          {isSuper && (
            <div className="flex flex-wrap gap-2 items-end">
              <div className="flex-1 min-w-[200px]"><Label>Add IP</Label>
                <Input data-testid="office-ip-input" value={newIp} onChange={(e) => setNewIp(e.target.value)} placeholder="e.g. 49.205.173.115" /></div>
              <Btn data-testid="add-ip-btn" variant="ghost" onClick={addIp}>Add</Btn>
              <Btn data-testid="add-my-ip-btn" variant="ghost" onClick={addThisIp}>Use my IP</Btn>
              <Btn data-testid="save-ips-btn" onClick={saveIps}>Save</Btn>
            </div>
          )}
        </Card>
      )}
    </div>
  );
}
